import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, MailOpen, Archive, Trash2, Inbox,
  CheckSquare, Square, X, RotateCcw,
} from 'lucide-react';
import { T, springs } from '../lib/tokens';
import { fmtRelative } from '../lib/helpers';
import { Glass } from '../components/glass/Glass';
import { Pill } from '../components/primitives/Badge';
import { Md } from '../components/primitives/Md';

const InboxActionBtn = ({ icon: Icon, label, onClick, danger }) => (
  <button
    onClick={onClick}
    title={label}
    style={{
      padding: '6px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
      background: 'transparent', color: danger ? '#dc2626' : T.textMid,
      display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontFamily: 'inherit',
      transition: 'all 0.15s',
    }}
    onMouseEnter={e => { e.currentTarget.style.background = danger ? 'rgba(220,38,38,0.08)' : 'rgba(0,0,0,0.05)'; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
  >
    <Icon size={14} />
    {label && <span>{label}</span>}
  </button>
);

export const InboxView = ({
  inbox,
  onMarkRead, onMarkUnread, onArchive, onDelete, onReorder,
}) => {
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const filtered = inbox.filter(i => {
    if (filter === 'unread') return !i.is_read;
    if (filter === 'archived') return i.is_archived;
    if (filter === 'briefs') return !i.is_archived && i.subject?.includes('Brief');
    if (filter === 'liken') return !i.is_archived && i.from_name === 'Liken Ops';
    if (filter === 'all') return !i.is_archived;
    return true;
  });

  const toggleSelect = (e, id) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(i => i.id)));
  };

  const selectItem = (item) => {
    setSelected(item);
    if (!item.is_read) onMarkRead(item.id);
  };

  const handleBulkAction = (action) => {
    const ids = [...selectedIds];
    if (action === 'read') ids.forEach(id => onMarkRead(id));
    if (action === 'unread') ids.forEach(id => onMarkUnread(id));
    if (action === 'archive') {
      ids.forEach(id => onArchive(id));
      if (selected && ids.includes(selected.id)) setSelected(null);
    }
    if (action === 'delete') {
      ids.forEach(id => onDelete(id));
      if (selected && ids.includes(selected.id)) setSelected(null);
    }
    setSelectedIds(new Set());
  };

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!filtered.length) return;
    const currentIdx = selected ? filtered.findIndex(i => i.id === selected.id) : -1;
    if (e.key === 'ArrowDown' || e.key === 'j') {
      e.preventDefault();
      selectItem(filtered[currentIdx < filtered.length - 1 ? currentIdx + 1 : 0]);
    } else if (e.key === 'ArrowUp' || e.key === 'k') {
      e.preventDefault();
      selectItem(filtered[currentIdx > 0 ? currentIdx - 1 : filtered.length - 1]);
    } else if (e.key === 'e' && selected) {
      e.preventDefault();
      onArchive(selected.id);
      setSelected(null);
    } else if (e.key === 'r' && selected) {
      e.preventDefault();
      if (selected.is_read) onMarkUnread(selected.id);
      else onMarkRead(selected.id);
    }
  }, [filtered, selected]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Drag-and-drop
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };
  const handleDrop = (e, toIdx) => {
    e.preventDefault();
    if (dragIdx !== null && dragIdx !== toIdx) onReorder(filtered[dragIdx].id, filtered[toIdx].id);
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const hasSelection = selectedIds.size > 0;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: T.text, letterSpacing: '-0.03em' }}>Inbox</h1>
        <p style={{ fontSize: 14, color: T.textMuted, marginTop: 4 }}>
          {inbox.filter(i => !i.is_read && !i.is_archived).length} unread · {inbox.filter(i => !i.is_archived).length} total
        </p>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          ['all', 'All'],
          ['unread', 'Unread'],
          ['archived', 'Archived'],
          ['briefs', 'Briefs'],
          ['liken', 'Liken'],
        ].map(([val, label]) => (
          <Pill key={val} active={filter === val} onClick={() => { setFilter(val); setSelectedIds(new Set()); setSelected(null); }}>
            {label}
          </Pill>
        ))}
      </div>

      {/* Bulk action bar */}
      <AnimatePresence>
        {hasSelection && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            style={{
              display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12,
              padding: '6px 12px', borderRadius: 8,
              background: 'rgba(212,146,30,0.06)', border: '1px solid rgba(212,146,30,0.15)',
            }}
          >
            <span style={{ fontSize: 12, color: T.textMid, marginRight: 8 }}>{selectedIds.size} selected</span>
            <InboxActionBtn icon={MailOpen} label="Read" onClick={() => handleBulkAction('read')} />
            <InboxActionBtn icon={Mail} label="Unread" onClick={() => handleBulkAction('unread')} />
            <InboxActionBtn icon={Archive} label="Archive" onClick={() => handleBulkAction('archive')} />
            <InboxActionBtn icon={Trash2} label="Delete" onClick={() => handleBulkAction('delete')} danger />
            <button onClick={() => setSelectedIds(new Set())} style={{
              marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, padding: 4,
            }}><X size={14} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ display: 'flex', gap: 20, flex: 1, minHeight: 0 }}>
        {/* List */}
        <Glass style={{ width: 360, flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} hover={false}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 18px',
            borderBottom: '1px solid rgba(0,0,0,0.05)', fontSize: 12, color: T.textMuted,
          }}>
            <button onClick={selectAll} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: T.textMid }}>
              {selectedIds.size === filtered.length && filtered.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}
            </button>
            <span>{selectedIds.size === filtered.length && filtered.length > 0 ? 'Deselect all' : 'Select all'}</span>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.02, ...springs.gentle }}
                draggable
                onDragStart={() => handleDragStart(i)}
                onDragOver={e => handleDragOver(e, i)}
                onDragEnd={handleDragEnd}
                onDrop={e => handleDrop(e, i)}
                onClick={() => selectItem(item)}
                style={{
                  padding: '14px 18px',
                  borderBottom: i < filtered.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                  cursor: 'pointer',
                  background: selected?.id === item.id ? 'rgba(212,146,30,0.06)'
                    : dragOverIdx === i ? 'rgba(212,146,30,0.04)'
                    : !item.is_read ? 'rgba(255,255,255,0.5)' : 'transparent',
                  transition: 'background 0.15s',
                  opacity: dragIdx === i ? 0.5 : 1,
                  borderTop: dragOverIdx === i && dragIdx !== null ? '2px solid rgba(212,146,30,0.4)' : '2px solid transparent',
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                }}
                onMouseEnter={e => { if (selected?.id !== item.id && dragIdx === null) e.currentTarget.style.background = 'rgba(0,0,0,0.02)'; }}
                onMouseLeave={e => { if (selected?.id !== item.id && dragIdx === null) e.currentTarget.style.background = !item.is_read ? 'rgba(255,255,255,0.5)' : 'transparent'; }}
              >
                <button onClick={e => toggleSelect(e, item.id)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0 0 0',
                  color: selectedIds.has(item.id) ? T.amber : T.textLight, flexShrink: 0,
                }}>
                  {selectedIds.has(item.id) ? <CheckSquare size={14} /> : <Square size={14} />}
                </button>
                <div style={{ paddingTop: 6, flexShrink: 0 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: item.is_read ? 'transparent' : T.amber }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: item.is_read ? 400 : 600, color: T.text }}>{item.from_name}</span>
                    <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0 }}>{fmtRelative(item.created_at)}</span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: item.is_read ? 400 : 500, color: T.textMid, marginBottom: 3 }}>{item.subject}</div>
                  <div style={{ fontSize: 12, color: T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.preview || item.body?.slice(0, 80)}
                  </div>
                </div>
              </motion.div>
            ))}
            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                <Inbox size={28} color={T.textLight} style={{ marginBottom: 10 }} />
                <div style={{ fontSize: 13, color: T.textMuted }}>
                  {filter === 'archived' ? 'No archived items.' : 'Nothing here yet.'}
                </div>
              </div>
            )}
          </div>
        </Glass>

        {/* Detail */}
        <AnimatePresence mode="wait">
          {selected ? (
            <motion.div key={selected.id} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }} transition={springs.gentle} style={{ flex: 1 }}>
              <Glass style={{ padding: 28, overflowY: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }} hover={false}>
                <div style={{
                  display: 'flex', gap: 4, marginBottom: 16, paddingBottom: 12,
                  borderBottom: '1px solid rgba(0,0,0,0.05)',
                }}>
                  {selected.is_read
                    ? <InboxActionBtn icon={Mail} label="Mark unread" onClick={() => { onMarkUnread(selected.id); setSelected({ ...selected, is_read: false }); }} />
                    : <InboxActionBtn icon={MailOpen} label="Mark read" onClick={() => { onMarkRead(selected.id); setSelected({ ...selected, is_read: true }); }} />
                  }
                  {selected.is_archived
                    ? <InboxActionBtn icon={RotateCcw} label="Unarchive" onClick={() => { onArchive(selected.id, false); setSelected({ ...selected, is_archived: false }); }} />
                    : <InboxActionBtn icon={Archive} label="Archive" onClick={() => { onArchive(selected.id); setSelected(null); }} />
                  }
                  <InboxActionBtn icon={Trash2} label="Delete" onClick={() => { onDelete(selected.id); setSelected(null); }} danger />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>
                    {selected.from_name} · {fmtRelative(selected.created_at)}
                  </div>
                  <h2 style={{ fontSize: 20, fontWeight: 600, color: T.text, letterSpacing: '-0.02em', lineHeight: 1.3 }}>{selected.subject}</h2>
                </div>
                <div style={{ flex: 1 }}><Md>{selected.body}</Md></div>
              </Glass>
            </motion.div>
          ) : (
            <Glass style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }} hover={false}>
              <div style={{ textAlign: 'center' }}>
                <Mail size={32} color={T.textLight} style={{ marginBottom: 12 }} />
                <div style={{ fontSize: 14, color: T.textMuted }}>Select a message to read</div>
                <div style={{ fontSize: 12, color: T.textLight, marginTop: 6 }}>j/k to navigate, e to archive, r to toggle read</div>
              </div>
            </Glass>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
